import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface ServicePageProps {
  title: string;
  subtitle: string;
  description: string;
  heroImage: string;
  keywords: string[];
  targetAudience: { icon: string; title: string; desc: string }[];
  advantages: { icon: string; title: string; desc: string }[];
  faqs: FAQItem[];
  processSteps?: { step: string; title: string; desc: string }[];
  relatedServices?: { label: string; href: string }[];
}

function FAQAccordion({ faqs }: { faqs: FAQItem[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-3">
      {faqs.map((faq, i) => (
        <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-[#F5F7FA] transition-colors cursor-pointer"
            onClick={() => setOpen(open === i ? null : i)}
          >
            <span className="text-[#0A2540] font-medium text-sm pr-4">{faq.question}</span>
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className={`text-[#0A2540] text-base transition-transform duration-200 ${open === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
            </div>
          </button>
          {open === i && (
            <div className="px-6 py-4 bg-[#F5F7FA] border-t border-gray-100">
              <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ContactCTA() {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) return;
    if (form.message.length > 500) return;
    setStatus('loading');
    try {
      const body = new URLSearchParams({ name: form.name, phone: form.phone, message: form.message });
      const res = await fetch('https://readdy.ai/api/form/d7c144qqofe8vli40s7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setStatus('success'); setForm({ name: '', phone: '', message: '' }); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  return (
    <section className="py-20 bg-[#0A2540]">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-semibold tracking-widest text-[#60A5FA] uppercase">Ücretsiz Danışmanlık</span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-3 mb-4">
              Bu Konuda Destek Almak İster misiniz?
            </h2>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Sorularınızı bize iletin, uzman ekibimiz en kısa sürede size dönüş yapsın. İlk görüşme ücretsizdir.
            </p>
            <div className="flex flex-col gap-3">
              {[
                { icon: 'ri-phone-line', text: '0 530 095 32 30' },
                { icon: 'ri-mail-line', text: 'info@ozgurcakmak.com.tr' },
                { icon: 'ri-time-line', text: 'Pzt–Cum: 09:00–18:00' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
                    <i className={`${item.icon} text-white text-sm`}></i>
                  </div>
                  <span className="text-white/70 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
            <a
              href="https://wa.me/905300953230"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 mt-6 px-5 py-3 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:-translate-y-0.5 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              WhatsApp ile Yaz
            </a>
          </div>
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            id="service-contact-form"
            className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-4"
          >
            <div>
              <label className="text-white/60 text-xs mb-1.5 block">Ad Soyad</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="Adınız Soyadınız"
                className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors"
              />
            </div>
            <div>
              <label className="text-white/60 text-xs mb-1.5 block">Telefon</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                placeholder="+90 5XX XXX XX XX"
                className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors"
              />
            </div>
            <div>
              <div className="flex justify-between mb-1.5">
                <label className="text-white/60 text-xs">Mesajınız</label>
                <span className="text-white/30 text-xs">{form.message.length}/500</span>
              </div>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={4}
                placeholder="Danışmak istediğiniz konu..."
                className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors resize-none"
              ></textarea>
            </div>
            {status === 'success' && (
              <div className="flex items-center gap-2 p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg">
                <i className="ri-checkbox-circle-line text-emerald-400"></i>
                <span className="text-emerald-300 text-sm">Mesajınız iletildi. En kısa sürede dönüş yapacağız.</span>
              </div>
            )}
            {status === 'error' && (
              <div className="p-3 bg-rose-500/20 border border-rose-500/30 rounded-lg">
                <span className="text-rose-300 text-sm">Bir hata oluştu. Lütfen tekrar deneyin.</span>
              </div>
            )}
            <button
              type="submit"
              disabled={status === 'loading' || form.message.length > 500}
              className="w-full py-3 bg-white text-[#0A2540] text-sm font-semibold rounded-lg hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {status === 'loading' ? 'Gönderiliyor...' : 'Mesaj Gönder'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default function ServicePageLayout({
  title,
  subtitle,
  description,
  heroImage,
  keywords,
  targetAudience,
  advantages,
  faqs,
  processSteps,
  relatedServices,
}: ServicePageProps) {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <ServiceNavbar />

      {/* Hero */}
      <section className="relative pt-20 min-h-[480px] md:min-h-[560px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt={title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 via-[#0A2540]/70 to-[#0A2540]/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20">
          <nav className="flex items-center gap-2 mb-6" aria-label="breadcrumb">
            <Link to="/" className="text-white/50 text-xs hover:text-white/80 transition-colors">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line text-white/30 text-xs"></i>
            <span className="text-white/70 text-xs">{title}</span>
          </nav>
          <div className="flex flex-wrap gap-2 mb-5">
            {keywords.map((kw) => (
              <span key={kw} className="px-3 py-1 bg-white/10 border border-white/20 text-white/80 text-xs rounded-full">{kw}</span>
            ))}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 max-w-2xl leading-tight">{title}</h1>
          <p className="text-white/70 text-base md:text-lg max-w-xl leading-relaxed mb-8">{subtitle}</p>
          <div className="flex flex-wrap gap-3">
            <a
              href="#iletisim-cta"
              onClick={(e) => { e.preventDefault(); document.getElementById('iletisim-cta')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="px-6 py-3 bg-white text-[#0A2540] text-sm font-semibold rounded-xl hover:-translate-y-0.5 transition-all cursor-pointer whitespace-nowrap"
            >
              Bilgi Al
            </a>
            <a
              href="https://wa.me/905300953230"
              target="_blank"
              rel="nofollow noreferrer"
              className="px-6 py-3 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:-translate-y-0.5 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-whatsapp-line"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <p className="text-gray-600 text-base leading-relaxed text-center">{description}</p>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">Hedef Kitle</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mt-3">Kimler İçin Uygun?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {targetAudience.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="w-11 h-11 flex items-center justify-center bg-[#0A2540]/5 rounded-xl mb-4">
                  <i className={`${item.icon} text-[#0A2540] text-xl`}></i>
                </div>
                <h3 className="text-[#0A2540] font-semibold text-sm mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">Avantajlar</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mt-3">Bu Hizmetin Avantajları</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((item, i) => (
              <div key={i} className="flex flex-col items-start p-6 border border-gray-100 rounded-2xl hover:border-[#0A2540]/20 transition-colors">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0A2540] rounded-xl mb-4">
                  <i className={`${item.icon} text-white text-base`}></i>
                </div>
                <h3 className="text-[#0A2540] font-semibold text-sm mb-2">{item.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      {processSteps && processSteps.length > 0 && (
        <section className="py-16 bg-[#F5F7FA]">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">Süreç</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mt-3">Nasıl İlerleriz?</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, i) => (
                <div key={i} className="relative bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="text-4xl font-black text-[#0A2540]/5 mb-3">{step.step}</div>
                  <h3 className="text-[#0A2540] font-semibold text-sm mb-2">{step.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">SSS</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mt-3">Sık Sorulan Sorular</h2>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>

      {/* Related Services */}
      {relatedServices && relatedServices.length > 0 && (
        <section className="py-12 bg-[#F5F7FA]">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <h3 className="text-[#0A2540] font-semibold text-sm mb-5 text-center">İlgili Hizmetler</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {relatedServices.map((s) => (
                <Link
                  key={s.href}
                  to={s.href}
                  className="px-4 py-2 bg-white border border-gray-200 text-[#0A2540] text-sm rounded-lg hover:border-[#0A2540] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {s.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <div id="iletisim-cta">
        <ContactCTA />
      </div>

      {/* Footer */}
      <ServiceFooter />

      {/* Sticky WhatsApp */}
      <a
        href="https://wa.me/905300953230"
        target="_blank"
        rel="nofollow noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-[#25D366] text-white rounded-full cursor-pointer hover:scale-110 transition-transform"
        aria-label="WhatsApp"
      >
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>
    </div>
  );
}

function ServiceNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const serviceLinks = [
    { label: 'Şahıs Şirketi', href: '/sahis-sirketi-kurulusu' },
    { label: 'Limited Şirket', href: '/limited-sirket-kurulusu' },
    { label: 'Vergi Danışmanlığı', href: '/vergi-danismanligi' },
    { label: 'Muhasebe', href: '/genel-muhasebe' },
    { label: 'SGK & Bordro', href: '/sgk-bordro' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white border-b border-gray-100' : 'bg-[#0A2540]'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/20f355b3-57c6-42af-8f1b-e19ef75cee72/a013bf3f-953f-4147-a966-2d3225ff6ac1_zgr-akmak-logo-3.png?v=001ea3c7f21fa91e8ebb91c8becb8de9"
            alt="Özgür Çakmak SMMM Logo"
            className="h-12 w-auto object-contain"
          />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          {serviceLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-xs font-medium transition-colors hover:text-[#3B82F6] cursor-pointer whitespace-nowrap ${scrolled ? 'text-[#0A2540]' : 'text-white/80'}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#0A2540]' : 'text-white/80'}`}>
            Ana Sayfa
          </Link>
          <a
            href="#iletisim-cta"
            onClick={(e) => { e.preventDefault(); document.getElementById('iletisim-cta')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="px-5 py-2 bg-[#0A2540] text-white text-sm font-medium rounded-lg hover:-translate-y-0.5 transition-all whitespace-nowrap cursor-pointer border border-white/20"
          >
            İletişime Geç
          </a>
        </div>
        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${scrolled ? 'text-[#0A2540]' : 'text-white'}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menü"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-3">
          <Link to="/" className="text-[#0A2540] text-sm font-medium py-2 border-b border-gray-50 cursor-pointer">Ana Sayfa</Link>
          {serviceLinks.map((link) => (
            <Link key={link.href} to={link.href} className="text-[#0A2540] text-sm font-medium py-2 border-b border-gray-50 cursor-pointer">
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}

function ServiceFooter() {
  const services = [
    { label: 'Şahıs Şirketi Kuruluşu', href: '/sahis-sirketi-kurulusu' },
    { label: 'Limited Şirket Kuruluşu', href: '/limited-sirket-kurulusu' },
    { label: 'Vergi Danışmanlığı', href: '/vergi-danismanligi' },
    { label: 'Genel Muhasebe', href: '/genel-muhasebe' },
    { label: 'SGK ve Bordro', href: '/sgk-bordro' },
    { label: 'E-Fatura / E-Arşiv', href: '/e-fatura-e-arsiv' },
    { label: 'Beyanname Hizmetleri', href: '/beyanname-hizmetleri' },
    { label: 'Mali Analiz', href: '/mali-analiz' },
    { label: 'Teşvik Danışmanlığı', href: '/tesvik-danismanligi' },
    { label: 'Şirket Türü Seçimi', href: '/sirket-turu-secimi' },
  ];

  return (
    <footer className="bg-[#F5F7FA] border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center mb-4">
              <img
                src="https://storage.readdy-site.link/project_files/20f355b3-57c6-42af-8f1b-e19ef75cee72/a013bf3f-953f-4147-a966-2d3225ff6ac1_zgr-akmak-logo-3.png?v=001ea3c7f21fa91e8ebb91c8becb8de9"
                alt="Özgür Çakmak SMMM Logo"
                className="h-10 w-auto object-contain"
              />
            </div>
            <p className="text-gray-500 text-sm leading-relaxed">Mali süreçlerinizde güvenilir, şeffaf ve sürdürülebilir danışmanlık hizmeti.</p>
          </div>
          <div>
            <h4 className="text-[#0A2540] font-semibold text-sm mb-4">Hizmetler</h4>
            <ul className="grid grid-cols-2 gap-2">
              {services.map((s) => (
                <li key={s.href}>
                  <Link to={s.href} className="text-gray-500 text-xs hover:text-[#0A2540] transition-colors cursor-pointer">{s.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-[#0A2540] font-semibold text-sm mb-4">İletişim</h4>
            <div className="space-y-3">
              {[
                { icon: 'ri-map-pin-line', text: 'Aydınlı Mah. Mayıs Sok. Adres Lobi Sitesi B Blok No:2B/4 Tuzla/İstanbul' },
                { icon: 'ri-phone-line', text: '0 530 095 32 30' },
                { icon: 'ri-mail-line', text: 'info@ozgurcakmak.com.tr' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                    <i className={`${item.icon} text-sm`}></i>
                  </div>
                  <span className="text-gray-500 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-gray-400 text-xs">© 2026 Akın SMMM. Tüm hakları saklıdır.</p>
          <Link to="/" className="text-gray-400 text-xs hover:text-gray-600 transition-colors cursor-pointer">Ana Sayfaya Dön</Link>
        </div>
      </div>
    </footer>
  );
}
